import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { Observable, map } from 'rxjs';
import {
  NotificationDesignation,
  NotificationEvent,
  NotificationEventSetting,
  NotificationPreview,
  NotificationPreviewQuery,
  NotificationSettingLine,
  NotificationSettingLinePayload,
} from './notification-setting.model';
import { canonicalRole, canonicalRoles, formerRoleName } from '@app/features/users/role-names';

export interface INotificationSettingService {
  list(): Observable<NotificationEventSetting[]>;
  get(event: NotificationEvent): Observable<NotificationEventSetting>;
  preview(
    event: NotificationEvent,
    query: NotificationPreviewQuery,
  ): Observable<NotificationPreview>;
  createLine(
    event: NotificationEvent,
    payload: NotificationSettingLinePayload,
  ): Observable<NotificationSettingLine>;
  updateLine(
    lineId: string,
    payload: NotificationSettingLinePayload,
  ): Observable<NotificationSettingLine>;
  deleteLine(lineId: string): Observable<void>;
}

/**
 * Le réglage des notifications : consultable par tout utilisateur interne, modifiable par le
 * directeur général, le directeur RH et le gestionnaire des processus (RG-notification-droit-de-regler).
 */
@Injectable({ providedIn: 'root' })
export class NotificationSettingService implements INotificationSettingService {
  private http = inject(HttpClient);
  private url = `${inject(RUNTIME_CONFIG).apiUrl}/notification-settings`;

  list(): Observable<NotificationEventSetting[]> {
    return this.http
      .get<NotificationEventSetting[]>(this.url)
      .pipe(map((all) => all.map(readSetting)));
  }

  get(event: NotificationEvent): Observable<NotificationEventSetting> {
    return this.http.get<NotificationEventSetting>(`${this.url}/${event}`).pipe(map(readSetting));
  }

  preview(
    event: NotificationEvent,
    query: NotificationPreviewQuery,
  ): Observable<NotificationPreview> {
    return this.http
      .post<NotificationPreview>(`${this.url}/${event}/preview`, query)
      .pipe(map(readPreview));
  }

  createLine(
    event: NotificationEvent,
    payload: NotificationSettingLinePayload,
  ): Observable<NotificationSettingLine> {
    return this.http
      .post<NotificationSettingLine>(`${this.url}/${event}/lines`, writePayload(payload))
      .pipe(map(readLine));
  }

  updateLine(
    lineId: string,
    payload: NotificationSettingLinePayload,
  ): Observable<NotificationSettingLine> {
    return this.http
      .put<NotificationSettingLine>(`${this.url}/lines/${lineId}`, writePayload(payload))
      .pipe(map(readLine));
  }

  deleteLine(lineId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/lines/${lineId}`);
  }
}

// Les profils de l'API sont des rôles de CDT : lus sous leur nouveau nom, écrits sous l'ancien,
// que l'API accepte avant comme après vanex-cdt-back#338 (cadrage 2026-031, décision 02).

function readDesignation(designation: NotificationDesignation): NotificationDesignation {
  return designation.role ? { ...designation, role: canonicalRole(designation.role) } : designation;
}

function readLine(line: NotificationSettingLine): NotificationSettingLine {
  return { ...line, designations: line.designations.map(readDesignation) };
}

function readSetting(setting: NotificationEventSetting): NotificationEventSetting {
  return {
    ...setting,
    designableRoles: canonicalRoles(setting.designableRoles),
    lines: setting.lines.map(readLine),
  };
}

function readPreview(preview: NotificationPreview): NotificationPreview {
  return {
    ...preview,
    lines: preview.lines.map(readLine),
    recipients: preview.recipients.map((person) => ({
      ...person,
      reasons: person.reasons.map((reason) =>
        reason.role ? { ...reason, role: canonicalRole(reason.role) } : reason,
      ),
    })),
  };
}

function writePayload(payload: NotificationSettingLinePayload): NotificationSettingLinePayload {
  return {
    ...payload,
    designations: payload.designations.map((designation) =>
      designation.role ? { ...designation, role: formerRoleName(designation.role) } : designation,
    ),
  };
}
